import { type Point, point } from '../elements';
import type { Bounds } from '../elements/bounds';
import { snapToGrid } from '../grid';
import { getGraphFonts } from '../svg/text';
import { getRecordEntries, getRecordKeys } from '../types/record';
import { NODE_LABEL_FONT_SIZE } from './constants';
import { nodeTypeIcons } from './icons/icons';
import { NODE_LABEL_PADDING } from './icons/style';
import type { NodeData, NodeId, NodePositions } from './types';

interface NodeWithPosition {
  position?: Point;
}

type NodesWithPosition = {
  [node in NodeId]?: NodeWithPosition;
};

export const getNodeDataBounds = (nodeData: NodeData): Bounds => {
  const labelMetrics = getGraphFonts().nodeLabels.getMetrics(nodeData.label, {
    fontSize: NODE_LABEL_FONT_SIZE,
  });

  const iconSize = nodeTypeIcons[nodeData.type].getSize(nodeData.style);
  const halfWidth = Math.max(iconSize.width, labelMetrics.width) / 2;
  const halfHeight = iconSize.height / 2;
  return {
    left: nodeData.position.x - halfWidth,
    top:
      nodeData.position.y -
      halfHeight -
      NODE_LABEL_PADDING -
      labelMetrics.height,
    right: nodeData.position.x + halfWidth,
    bottom: nodeData.position.y + halfHeight,
  };
};

const shiftNode = (node: Point, shiftBy: Partial<Point>) =>
  snapToGrid(point.shift(node, shiftBy));

export const shiftNodes = (
  nodes: NodePositions,
  shiftBy: Partial<Point>,
): NodePositions => {
  const result: NodePositions = { ...nodes };
  for (const [nodeSn, nodePosition] of getRecordEntries(result)) {
    result[nodeSn] = shiftNode(nodePosition!, shiftBy);
  }
  return result;
};

export const shiftNodesWithPosition = <TNode extends NodesWithPosition>(
  nodes: TNode,
  shiftBy: Partial<Point>,
): TNode => {
  const result = { ...nodes };
  for (const nodeId of getRecordKeys(result)) {
    if ('position' in result[nodeId]!) {
      result[nodeId]!.position = shiftNode(result[nodeId]!.position!, shiftBy);
    }
  }
  return result;
};
